// myReactLib/MainBlocks.js — the big composite sections of the W4U site.
//
// Components: Navigation, HeroCarousel, DescriptionBoxes, SloganRotator,
//             Subsections (+ Slider + SubsectionBox), EndOfPageMessage,
//             Footer, PlaceholderPage.

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { Link } from 'react-router-dom';
import { OrangeButton } from './BasicItems';
// circular import (App imports this module too) — safe: ConfigContext is only touched
// at render time, long after both modules finished evaluating (ESM live bindings)
import { ConfigContext } from '../App';

// ============================================================
//  RICH-TEXT HELPERS  — applied to every user-content string.
// ============================================================
//
// Two layers of "richness" on JSON-driven text fields:
//
//   1. Format-aware field shape.
//      A text field can be either a plain string (legacy) or an object
//      with `text` and optional `format`:
//         "title": "Small Automations"
//         "title": {"text": "Small Automations",
//                   "format": {"size": "3rem", "font": "Inter"}}
//      Use `getText(field)` to read the value, `getStyle(field)` for
//      inline style (fontSize/fontFamily). Both tolerate either shape.
//
//   2. Inline markup inside the text itself, parsed by parseRichText():
//         _word_          → italic
//         *word*          → medium weight (font-weight: 500)
//         **word**        → bold
//         <word>          → orange (W4U accent)
//         ~word~          → underline
//      They can overlap/nest in any order — markers toggle the matching
//      style on/off as the parser walks the string. Example:
//         "Software That <**actually _works for you_**>"
//      renders "actually" bold+orange, "works for you" bold+italic+orange.
//      A marker can be escaped to render literally: \* \_ \< \> \~ .
//      \n in the JSON string also keeps working (CSS `white-space: pre-line`).

function getText(field, fallback = '') {
  if (field == null) return fallback;
  if (typeof field === 'string') return field;
  if (typeof field === 'object' && 'text' in field) return field.text || fallback;
  return fallback;
}

function getStyle(field) {
  if (!field || typeof field !== 'object') return undefined;
  const fmt = field.format;
  if (!fmt) return undefined;
  const style = {};
  if (fmt.size)        style.fontSize = fmt.size;
  if (fmt.font)        style.fontFamily = fmt.font;
  if (fmt.weight)      style.fontWeight = fmt.weight;
  if (fmt.font_style)  style.fontStyle = fmt.font_style;
  if (fmt.line_height) style.lineHeight = fmt.line_height;
  if (fmt.letter_spacing) style.letterSpacing = fmt.letter_spacing;
  // Optional spacing to the next block — yaml: bottom_margin inside format/format_override.
  if (fmt.margin_bottom)       style.marginBottom = fmt.margin_bottom;
  // Mobile overrides — CSS picks these up via a media query that targets any
  // element carrying --fs-mobile / --ls-mobile / --lh-mobile / --mb-mobile in its inline style.
  if (fmt.size_mobile)           style['--fs-mobile'] = fmt.size_mobile;
  if (fmt.letter_spacing_mobile) style['--ls-mobile'] = fmt.letter_spacing_mobile;
  if (fmt.line_height_mobile)    style['--lh-mobile'] = fmt.line_height_mobile;
  if (fmt.margin_bottom_mobile)  style['--mb-mobile'] = fmt.margin_bottom_mobile;
  return Object.keys(style).length ? style : undefined;
}

// ============================================================
//  LANGUAGE RESOLUTION  (bilingual content → active language)
// ============================================================
//
// The backend sends every text element in BOTH languages:
//     { eng: { text, format }, ita: { text, format } }
// resolveBilingualTree() walks the whole payload once and replaces each such
// node with the active language's { text, format }. Everything downstream
// (getText / getStyle / RT / all components) then sees the same single-language
// shape it always has — so nothing else needs to change. Legacy plain
// { text, format } or raw-string nodes pass through untouched, which lets
// collections be migrated to bilingual one at a time without breaking.

// A node is "bilingual" if it has eng & ita branches that look like text nodes.
function isBilingualNode(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if (!('eng' in value) || !('ita' in value)) return false;
  const probe = value.eng;
  return probe && typeof probe === 'object' &&
         ('text' in probe || 'format' in probe);
}

function resolveBilingualTree(data, lang) {
  if (Array.isArray(data)) return data.map((d) => resolveBilingualTree(d, lang));
  if (data && typeof data === 'object') {
    if (isBilingualNode(data)) {
      // pick the requested language, fall back to English
      return resolveBilingualTree(data[lang] || data.eng, lang);
    }
    const out = {};
    for (const [k, v] of Object.entries(data)) out[k] = resolveBilingualTree(v, lang);
    return out;
  }
  return data;
}

// Auto-detect the UI language from the browser. Italian browsers → 'ita',
// everyone else → 'eng'. (A future on-page switcher will just call setLang.)
function detectLanguage() {
  const nav = (typeof navigator !== 'undefined' &&
               (navigator.language || (navigator.languages && navigator.languages[0]))) || 'en';
  return nav.toLowerCase().startsWith('it') ? 'ita' : 'eng';
}

// Escaped markers (\* \_ \< \> \~) are swapped to private-use placeholder chars
// before tokenizing, then restored as literal characters in the output.
const ESCAPE_PLACEHOLDERS = { '*': '', '_': '', '<': '', '>': '', '~': '' };
const PLACEHOLDER_TO_CHAR = Object.fromEntries(Object.entries(ESCAPE_PLACEHOLDERS).map(([ch, ph]) => [ph, ch]));

function restoreEscapedMarkers(token) {
  return token.replace(/[-]/g, (ph) => PLACEHOLDER_TO_CHAR[ph] || ph);
}

function parseRichText(text) {
  if (text == null) return text;
  let str = String(text);
  if (!str.length) return str;
  // protect escaped markers from the tokenizer (e.g. \* renders a literal asterisk)
  str = str.replace(/\\([*_<>~])/g, (_, ch) => ESCAPE_PLACEHOLDERS[ch]);
  // ** has priority over * thanks to alternation order in the regex.
  const tokens = str.split(/(\*\*|\*|_|<|>|~)/g);
  const result = [];
  const active = { bold: false, medium: false, italic: false, orange: false, underline: false };
  let key = 0;
  for (const token of tokens) {
    if      (token === '**') active.bold      = !active.bold;
    else if (token === '*')  active.medium    = !active.medium;
    else if (token === '_')  active.italic    = !active.italic;
    else if (token === '~')  active.underline = !active.underline;
    else if (token === '<')  active.orange = true;
    else if (token === '>')  active.orange = false;
    else if (token) {
      const cls = [];
      if (active.bold)      cls.push('w4u-bold');
      if (active.medium)    cls.push('w4u-medium');
      if (active.italic)    cls.push('w4u-italic');
      if (active.orange)    cls.push('w4u-orange');
      if (active.underline) cls.push('w4u-underline');
      const literal = restoreEscapedMarkers(token);
      if (cls.length) {
        result.push(<span key={key++} className={cls.join(' ')}>{literal}</span>);
      } else {
        result.push(literal);
      }
    }
  }
  if (result.length === 0) return '';
  if (result.length === 1 && typeof result[0] === 'string') return result[0];
  return result;
}

// Convenience: render a possibly format-aware field's text with markup parsed.
const RT = (field) => parseRichText(getText(field));

// Yaml-driven section spacing: a `bottom_margin: { desktop, mobile }` block (plain CSS
// values) becomes inline margin-bottom + the --mb-mobile CSS var (consumed by the
// mobile media rule in index.css). Empty values fall through to the CSS defaults.
function getBottomMargin(bottomMargin) {
  if (!bottomMargin || typeof bottomMargin !== 'object') return undefined;
  const style = {};
  if (bottomMargin.desktop) style.marginBottom = bottomMargin.desktop;
  if (bottomMargin.mobile)  style['--mb-mobile'] = bottomMargin.mobile;
  return Object.keys(style).length ? style : undefined;
}

// ---------- Language toggle (monochrome-blue flags) ----------
// Collapsed = just the current language's flag (subtle, drawn in shades of blue so it blends with
// the navy site). Hover / focus / tap opens the list of languages, each flag + a small tag
// (ENG / ITA); the active one is highlighted. Desktop: sits between the nav links and the orange
// button. Mobile: lives in the hamburger menu, where the list is shown inline (no hover on touch).
const LANGUAGES = [{ code: 'eng', tag: 'ENG' }, { code: 'ita', tag: 'ITA' }];

// Flags in blue shades (not the national colours), so they sit quietly but still read as a flag:
// IT = three vertical bands; EN = a simplified Union Jack.
const Flag = ({ code }) => (
  code === 'ita' ? (
    <svg className="w4u-flag" viewBox="0 0 9 6" aria-hidden="true">
      <rect width="9" height="6" rx="0.6" fill="#274868" />
      <rect x="3" width="3" height="6" fill="#4f7099" />
      <rect x="6" width="3" height="6" fill="#86a6c8" />
    </svg>
  ) : (
    <svg className="w4u-flag" viewBox="0 0 9 6" aria-hidden="true">
      <rect width="9" height="6" rx="0.6" fill="#274868" />
      <path d="M0 0L9 6M9 0L0 6" stroke="#86a6c8" strokeWidth="1.4" />
      <rect x="3.7" width="1.6" height="6" fill="#86a6c8" />
      <rect y="2.2" width="9" height="1.6" fill="#86a6c8" />
      <rect x="4.05" width="0.9" height="6" fill="#3a5d85" />
      <rect y="2.55" width="9" height="0.9" fill="#3a5d85" />
    </svg>
  )
);

const LangToggle = ({ variant }) => {
  const { lang, setLang } = useContext(ConfigContext);
  if (!setLang) return null;
  return (
    <div className={`w4u-lang w4u-lang--${variant}`}>
      <button type="button" className="w4u-lang__current"
        aria-label={lang === 'ita' ? 'Cambia lingua' : 'Change language'}>
        <Flag code={lang} />
      </button>
      <div className="w4u-lang__menu" role="menu">
        {LANGUAGES.map((l) => (
          <button key={l.code} type="button" role="menuitem"
            className={'w4u-lang__item' + (l.code === lang ? ' w4u-lang__item--active' : '')}
            onClick={() => setLang(l.code)}>
            <Flag code={l.code} />
            <span className="w4u-lang__tag">{l.tag}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ---------- Navigation ----------
const Navigation = ({ menuBar, prefix }) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <nav className={'w4u-nav' + (open ? ' w4u-nav--open' : '')}>
      <div className="w4u-nav__inner">
        <Link to="/" className="w4u-nav__brand" onClick={close}>
          <img src={`${prefix}/logo192.png`} alt="WorksForYou logo" className="w4u-nav__logo" />
          <span>WorksForYou</span>
        </Link>
        <button
          type="button"
          className="w4u-nav__hamburger"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span aria-hidden="true">{open ? '✕' : '☰'}</span>
        </button>
        <ul className="w4u-nav__links">
          <li><Link to="/how-it-works"      className="w4u-nav__link" onClick={close} style={getStyle(menuBar.page_1_title)}>{RT(menuBar.page_1_title)}</Link></li>
          <li><Link to="/small-automations" className="w4u-nav__link" onClick={close} style={getStyle(menuBar.page_2_title)}>{RT(menuBar.page_2_title)}</Link></li>
          <li><Link to="/complex-systems"   className="w4u-nav__link" onClick={close} style={getStyle(menuBar.page_3_title)}>{RT(menuBar.page_3_title)}</Link></li>
          <li><Link to="/contact"           className="w4u-nav__link" onClick={close} style={getStyle(menuBar.contact_title)}>{getText(menuBar.contact_title) ? RT(menuBar.contact_title) : 'Contact'}</Link></li>
          <li className="w4u-nav__lang-item"><LangToggle variant="mobile" /></li>
        </ul>
        <LangToggle variant="desktop" />
        <OrangeButton to="/contact" onClick={close} style={getStyle(menuBar.right_side_button)}>{RT(menuBar.right_side_button)}</OrangeButton>
      </div>
    </nav>
  );
};

// ---------- HeroCarousel ----------

// Parse a concept-band item_size tweak ("0%" = default, "+20%" = 20% bigger,
// "-10%" = 10% smaller) into a scale factor for the CSS width calc.
function conceptBandScale(pct) {
  const n = parseFloat(String(pct || '').replace('%', '').replace('+', ''));
  return Number.isFinite(n) ? 1 + n / 100 : 1;
}

const HeroCarousel = ({ mainSection, autoRotateMs = 6000 }) => {
  const slides = (mainSection && mainSection.main_images) || [];
  const { lang } = useContext(ConfigContext);
  const [current, setCurrent] = useState(0);
  const [titleVisible, setTitleVisible] = useState(true);

  // rotation timing + fade come from main_section.yaml → rotation (seconds / crossfade_ms)
  const rot = (mainSection && mainSection.rotation) || {};
  const autoMs = parseFloat(rot.seconds) > 0 ? parseFloat(rot.seconds) * 1000 : autoRotateMs;
  const fadeMs = parseFloat(rot.crossfade_ms) > 0 ? parseFloat(rot.crossfade_ms) : 350;
  const titleGap = (mainSection && mainSection.title_gap) || {};

  // concept band image is bilingual: the backend emits image_path as { eng, ita }. Pick the
  // active language (fall back to english); a legacy plain-string image_path is used as-is.
  const conceptBandImg = mainSection && mainSection.concept_band && mainSection.concept_band.image_path;
  const conceptBandSrc = conceptBandImg
    ? (typeof conceptBandImg === 'string' ? conceptBandImg : (conceptBandImg[lang] || conceptBandImg.eng))
    : null;

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => {
      setTitleVisible(false);
      setTimeout(() => {
        setCurrent((c) => (c + 1) % slides.length);
        setTitleVisible(true);
      }, fadeMs);
    }, autoMs);
    return () => clearInterval(t);
  }, [slides.length, autoMs, fadeMs]);

  if (slides.length === 0) return null;
  const currentSlide = slides[current];

  const goTo = (i) => {
    if (i === current) return;
    setTitleVisible(false);
    setTimeout(() => { setCurrent(i); setTitleVisible(true); }, fadeMs);
  };

  return (
    <section className="w4u-hero" style={getBottomMargin(mainSection.bottom_margin)}>
      <div className="w4u-hero__carousel">
        {slides.map((slide, i) => (
          <div
            key={slide.name || i}
            className={'w4u-hero__slide' + (i === current ? ' w4u-hero__slide--active' : '')}
            style={{ backgroundImage: `url("${slide.image_path}")` }}
            aria-hidden={i !== current}
          />
        ))}
        <div className="w4u-hero__overlay" aria-hidden="true" />
        <div className="w4u-hero__text">
          {/* Concept band image (when enabled + found by the backend), else the textual subtitle. */}
          {conceptBandSrc ? (
            <img
              src={conceptBandSrc}
              alt={getText(mainSection.common_subtitle)}
              className="w4u-hero__concept-band"
              style={{
                '--cb-scale':        conceptBandScale((mainSection.concept_band.item_size || {}).desktop),
                '--cb-scale-mobile': conceptBandScale((mainSection.concept_band.item_size || {}).mobile),
              }}
            />
          ) : (
            <p className="w4u-hero__subtitle" style={getStyle(mainSection.common_subtitle)}>
              {RT(mainSection.common_subtitle)}
            </p>
          )}
          <h1 className="w4u-hero__title"
              style={{ ...getStyle(currentSlide.title), opacity: titleVisible ? 1 : 0,
                       transition: `opacity ${fadeMs}ms ease`,
                       marginTop: titleGap.desktop || undefined, '--htg-mobile': titleGap.mobile || undefined }}>
            {RT(currentSlide.title)}
          </h1>
        </div>
        {slides.length > 1 && (
          <div className="w4u-hero__controls">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                className={'w4u-hero__dot' + (i === current ? ' w4u-hero__dot--active' : '')}
                onClick={() => goTo(i)}
                aria-label={`Show slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      <OrangeButton to="/how-it-works" variant="hero" style={getStyle(mainSection.central_button_text)}>
        {RT(mainSection.central_button_text)}
      </OrangeButton>
    </section>
  );
};

// ---------- DescriptionBoxes ----------
// Own homepage module (4x2 grid, no slider). Sizes come from description_boxes.yaml → layout:
// the card width/height (content is centred, so a smaller box crops symmetrically — equalise rows
// via char_spacing), the icon image size, and the orange badge size (independent of the icon).
const DescriptionBoxes = ({ boxes, layout, bottomMargin }) => {
  if (!boxes || boxes.length === 0) return null;
  const lay = layout || {};
  const dt = (lay.box_size && lay.box_size.desktop) || {};
  const mb = (lay.box_size && lay.box_size.mobile) || {};
  const icon = lay.icon_size || {};
  const badge = lay.badge_size || {};
  // mobile sizes are converted px -> rem so they scale with the fluid root font-size (responsive):
  // a smaller phone shrinks the root, which shrinks these in step with the text.
  const toRem = (v) => (typeof v === 'string' && v.trim().endsWith('px')) ? (parseFloat(v) / 16) + 'rem' : v;
  const styleVars = { ...getBottomMargin(bottomMargin || lay.bottom_margin) };
  if (dt.width)      styleVars['--db-box-w']   = dt.width;
  if (dt.height)     styleVars['--db-box-h']   = dt.height;
  if (mb.width)      styleVars['--db-box-w-m'] = toRem(mb.width);
  if (mb.height)     styleVars['--db-box-h-m'] = toRem(mb.height);
  if (icon.desktop)  styleVars['--db-icon']    = icon.desktop;
  if (icon.mobile)   styleVars['--db-icon-m']  = toRem(icon.mobile);
  if (badge.desktop) styleVars['--db-badge']   = badge.desktop;
  if (badge.mobile)  styleVars['--db-badge-m'] = toRem(badge.mobile);
  return (
    <section className="w4u-boxes" style={styleVars}>
      <div className="w4u-boxes__grid">
        {boxes.map((box, i) => (
          <article key={i} className="w4u-box">
            <div className="w4u-box__badge">
              {box.image_path && <img src={box.image_path} alt="" className="w4u-box__icon" />}
            </div>
            <h3 className="w4u-box__title" style={getStyle(box.title)}>{RT(box.title)}</h3>
            <p className="w4u-box__body"  style={getStyle(box.body)}>{RT(box.body)}</p>
          </article>
        ))}
      </div>
    </section>
  );
};

// ---------- SloganRotator ----------
// Slogans are now a dict (slogans.json restructure) with shared format +
// numbered keys (slogan_1, slogan_2, ...). BackendPageForHP turns that into
// { lines: [...], format: {...} } and passes them here. Legacy list shape
// is still accepted in case prod hasn't updated yet.
const SloganRotator = ({ slogans, rotation, rotateMs = 7500 }) => {
  // Each slogan is now a resolved { text, format } node — style is per-slogan.
  const list = useMemo(() => (Array.isArray(slogans) ? slogans : []), [slogans]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  // rotation timing + fade come from slogans.yaml → rotation (seconds / crossfade_ms)
  const rot = rotation || {};
  const autoMs = parseFloat(rot.seconds) > 0 ? parseFloat(rot.seconds) * 1000 : rotateMs;
  const fadeMs = parseFloat(rot.crossfade_ms) > 0 ? parseFloat(rot.crossfade_ms) : 400;

  useEffect(() => {
    if (list.length <= 1) return;
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % list.length);
        setVisible(true);
      }, fadeMs);
    }, autoMs);
    return () => clearInterval(t);
  }, [list, autoMs, fadeMs]);

  if (list.length === 0) return null;
  const current = list[index];

  // hidden tester feature (kept in prod on purpose): click / tap the slogan to jump to the next one
  const goNext = () => {
    if (list.length <= 1) return;
    setVisible(false);
    setTimeout(() => { setIndex((i) => (i + 1) % list.length); setVisible(true); }, fadeMs);
  };

  return (
    <section className="w4u-slogan" onClick={goNext}>
      <p className="w4u-slogan__text" style={{ ...getStyle(current), opacity: visible ? 1 : 0, transition: `opacity ${fadeMs}ms ease` }}>
        {RT(current)}
      </p>
    </section>
  );
};

// ---------- Slider ----------
// Horizontal swipe/scroll track with DOTS underneath (no arrows) — the dots make it obvious there
// are more items, on desktop and mobile alike. One dot per item; the active dot tracks the scroll
// position; clicking a dot scrolls that item to the start. (Same idea as the CS mobile slider, but
// neutral colouring — muted dots, orange when active.)
const Slider = ({ children }) => {
  const items = React.Children.toArray(children);
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const strideOf = (track) => (track.children[1] ? track.children[1].offsetLeft - track.children[0].offsetLeft : (track.children[0] ? track.children[0].offsetWidth : 1)) || 1;
  const onScroll = () => {
    const track = trackRef.current;
    if (!track || !track.children.length) return;
    setActive(Math.max(0, Math.min(items.length - 1, Math.round(track.scrollLeft / strideOf(track)))));
  };
  const goTo = (i) => {
    const track = trackRef.current;
    if (!track || !track.children[i]) return;
    track.scrollTo({ left: track.children[i].offsetLeft - track.children[0].offsetLeft, behavior: 'smooth' });
  };
  return (
    <div className="w4u-slider">
      <div className="w4u-slider__track" ref={trackRef} onScroll={onScroll}>{children}</div>
      {items.length > 1 && (
        <div className="w4u-slider__dots">
          {items.map((_, i) => (
            <button key={i} type="button"
              className={'w4u-slider__dot' + (i === active ? ' w4u-slider__dot--active' : '')}
              aria-label={`Go to item ${i + 1}`} onClick={() => goTo(i)} />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- SubsectionBox ----------
const SubsectionBox = ({ box, linkTo }) => {
  // showcase = Complex Systems (has a subtitle); compact = Small Automations
  // (title + header only — the yaml field was renamed from 'body' to 'header').
  // When `linkTo` is set (subsections.yaml → link_to + the item tag) the whole box
  // becomes a Link to the matching section of the dedicated page.
  const isShowcase = box.subtitle !== undefined;
  const Outer = linkTo ? Link : 'article';
  const outerProps = linkTo ? { to: linkTo } : {};

  if (isShowcase) {
    return (
      <Outer className="w4u-subsection-box w4u-subsection-box--showcase" {...outerProps}>
        <h3 className="w4u-subsection-box__title" style={getStyle(box.title)}>{RT(box.title)}</h3>
        {box.subtitle != null && (
          <p className="w4u-subsection-box__subtitle" style={getStyle(box.subtitle)}>{RT(box.subtitle)}</p>
        )}
        {box.image_path && (
          <div className="w4u-subsection-box__image-wrap">
            <img src={box.image_path} alt="" className="w4u-subsection-box__image" />
          </div>
        )}
        {box.header != null && (
          // homepage CS box: ignore the \n line-breaks authored for the dedicated CS page (they
          // are tuned for the wider page layout and would break awkwardly in the narrow box)
          <p className="w4u-subsection-box__header" style={getStyle(box.header)}>{parseRichText(getText(box.header).replace(/\n/g, ' '))}</p>
        )}
      </Outer>
    );
  }

  return (
    <Outer className="w4u-subsection-box w4u-subsection-box--compact" {...outerProps}>
      <header className="w4u-subsection-box__compact-header">
        {box.image_path && (
          <img src={box.image_path} alt="" className="w4u-subsection-box__icon" />
        )}
        <h3 className="w4u-subsection-box__title" style={getStyle(box.title)}>{RT(box.title)}</h3>
      </header>
      {(box.header != null || box.body != null) && (
        <p className="w4u-subsection-box__body" style={getStyle(box.header || box.body)}>{RT(box.header || box.body)}</p>
      )}
    </Outer>
  );
};

// ---------- Subsections ----------
const Subsections = ({ subsections }) => {
  if (!subsections) return null;
  const visible = Object.entries(subsections)
    .filter(([key, ss]) =>
      key !== 'subsection_0' &&
      ss && ss.boxes_content && Object.keys(ss.boxes_content).length > 0
    );
  if (visible.length === 0) return null;
  return (
    <>
      {visible.map(([key, ss]) => (
        <Subsection key={key} subsection={ss} />
      ))}
    </>
  );
};

const Subsection = ({ subsection }) => {
  const { title, subtitle, boxes_size = {}, image_size = {}, boxes_content = {}, bottom_margin = {} } = subsection;
  // box + image sizes are split desktop / mobile (mobile px → rem so they scale with the fluid root
  // font-size, like the description boxes). Empty mobile values fall back to the desktop ones in CSS.
  const toRem = (v) => (typeof v === 'string' && v.trim().endsWith('px')) ? (parseFloat(v) / 16) + 'rem' : v;
  const bsD = boxes_size.desktop || {}, bsM = boxes_size.mobile || {};
  const imD = image_size.desktop || {}, imM = image_size.mobile || {};
  const styleVars = {};
  if (bsD.width)  styleVars['--box-width']     = bsD.width;
  if (bsD.height) styleVars['--box-height']    = bsD.height;
  if (bsM.width)  styleVars['--box-width-m']   = toRem(bsM.width);
  if (bsM.height) styleVars['--box-height-m']  = toRem(bsM.height);
  if (imD.width)  styleVars['--box-image-w']   = imD.width;
  if (imD.height) styleVars['--box-image-h']   = imD.height;
  if (imM.width)  styleVars['--box-image-w-m'] = toRem(imM.width);
  if (imM.height) styleVars['--box-image-h-m'] = toRem(imM.height);
  // spacing to the next section (desktop inline; mobile via the --mb-mobile media rule)
  if (bottom_margin.desktop) styleVars.marginBottom = bottom_margin.desktop;
  if (bottom_margin.mobile)  styleVars['--mb-mobile'] = bottom_margin.mobile;

  return (
    <section className="w4u-subsection" style={styleVars}>
      {title != null && (
        <h2 className="w4u-subsection__title" style={getStyle(title)}>{RT(title)}</h2>
      )}
      {subtitle != null && (
        <p className="w4u-subsection__subtitle" style={getStyle(subtitle)}>
          {RT(subtitle)}
        </p>
      )}
      <Slider>
        {Object.entries(boxes_content).map(([key, box]) => (
          <SubsectionBox key={key} box={box} linkTo={subsection.link_to ? `${subsection.link_to}#${key}` : null} />
        ))}
      </Slider>
    </section>
  );
};

// ---------- EndOfPageMessage ----------
// Reads from slogans.json → takeaway_message (3 lines: setup_1, setup_2,
// punch_line). Each line is a format-aware field with text + format.
const EndOfPageMessage = ({ takeaway }) => {
  if (!takeaway) return null;
  const { setup_line_1, setup_line_2, punch_line } = takeaway;
  return (
    <section className="w4u-closer" style={getBottomMargin(takeaway.bottom_margin)}>
      {setup_line_1 && (
        <p className="w4u-closer__line" style={getStyle(setup_line_1)}>{RT(setup_line_1)}</p>
      )}
      {setup_line_2 && (
        <p className="w4u-closer__line" style={getStyle(setup_line_2)}>{RT(setup_line_2)}</p>
      )}
      {punch_line && (
        <p className="w4u-closer__hook" style={getStyle(punch_line)}>{RT(punch_line)}</p>
      )}
    </section>
  );
};

// ---------- Footer ----------
const ICON_LINKEDIN = (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 17.78V10.6H6V17.78h2.34zM7.17 9.56a1.36 1.36 0 1 0 0-2.72 1.36 1.36 0 0 0 0 2.72zM18 17.78v-4.04c0-1.94-1.04-2.84-2.42-2.84a2.1 2.1 0 0 0-1.9 1.04V10.6H11.34c.03.66 0 7.18 0 7.18h2.34v-4.01c0-.21.02-.42.08-.57.17-.42.55-.85 1.2-.85.84 0 1.18.64 1.18 1.59v3.84H18z"/>
  </svg>
);

const Footer = ({ menuBar, config, contactData, footer }) => {
  // LinkedIn href comes from contact.json (single source of truth: it's the
  // same URL the contact page uses for the LinkedIn channel).
  // channels is a named dict ({ email, linkedin, based_in }) → read the linkedin entry directly
  const linkedinHref = contactData?.channels?.linkedin?.href || '#';
  const isExternal = linkedinHref.startsWith('http');
  // Small-print lines come from footer.yaml (render order = yaml order; empty text = hidden).
  // The version/privacy/terms line is app chrome and stays here (shows the live backend version).
  const footerLines = footer ? Object.values(footer).filter((line) => getText(line)) : [];
  // Top band — desktop: one row (page links + Contact | LinkedIn icon right).
  // Mobile: two centered lines — line 1 = the three page links, line 2 = Contact + LinkedIn
  // (the desktop-only Contact <li> and socials <div> are hidden on mobile by CSS, and the
  // mobile-only contact group is hidden on desktop).
  return (
  <footer className="w4u-footer">
    <div className="w4u-footer__top">
      <ul className="w4u-footer__links">
        <li><Link to="/how-it-works"      className="w4u-footer__link">{RT(menuBar.page_1_title)}</Link></li>
        <li><Link to="/small-automations" className="w4u-footer__link">{RT(menuBar.page_2_title)}</Link></li>
        <li><Link to="/complex-systems"   className="w4u-footer__link">{RT(menuBar.page_3_title)}</Link></li>
        <li className="w4u-footer__contact-desktop"><Link to="/contact" className="w4u-footer__link">{getText(menuBar.contact_title) ? RT(menuBar.contact_title) : 'Contact'}</Link></li>
      </ul>
      <div className="w4u-footer__socials">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          href={linkedinHref}
          className="w4u-footer__social"
          aria-label="LinkedIn"
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
        >{ICON_LINKEDIN}</a>
      </div>
      <div className="w4u-footer__contact-mobile">
        <Link to="/contact" className="w4u-footer__link">{getText(menuBar.contact_title) ? RT(menuBar.contact_title) : 'Contact'}</Link>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          href={linkedinHref}
          className="w4u-footer__social"
          aria-label="LinkedIn"
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
        >{ICON_LINKEDIN}</a>
      </div>
    </div>
    <div className="w4u-footer__meta">
      {footerLines.map((line, i) => (
        <p key={i} style={getStyle(line)}>{RT(line)}</p>
      ))}
      <p>
        Version {config?.BACKEND_VERSION || '—'}
        &nbsp;|&nbsp; <Link to="/privacy" className="w4u-footer__metalink">Privacy Policy</Link>
        &nbsp;|&nbsp; <Link to="/terms"   className="w4u-footer__metalink">Terms of Service</Link>
        &nbsp;|&nbsp; <Link to="/data-deletion" className="w4u-footer__metalink">Data Deletion</Link>
      </p>
    </div>
  </footer>
  );
};

// ---------- PlaceholderPage ----------
const PlaceholderPage = ({ title, subtitle = 'Coming soon!', menuBar, prefix, config, contactData, footer }) => (
  <div className="w4u-page">
    <Navigation menuBar={menuBar} prefix={prefix} />
    <main className="w4u-main">
      <div className="w4u-placeholder">
        <h1 className="w4u-placeholder__title">{title}</h1>
        <p className="w4u-placeholder__text">{subtitle}</p>
      </div>
    </main>
    <Footer menuBar={menuBar} config={config} contactData={contactData} footer={footer} />
  </div>
);

export {
  Navigation,
  HeroCarousel,
  DescriptionBoxes,
  SloganRotator,
  Subsections,
  EndOfPageMessage,
  Footer,
  PlaceholderPage,
  // Rich-text helpers (used by other pages too — e.g. BackendPageForContact).
  getText,
  getStyle,
  getBottomMargin,
  parseRichText,
  RT,
  // Language helpers (used by App.js to resolve bilingual payloads).
  resolveBilingualTree,
  detectLanguage,
};
